<template>
  <ticket-preview-base
    :ticket="ticket"
    :loading="ticketDetailsLoading"
    :ticket-details="ticketDetails"
    :selected-tab="selectedTab"
    :tabs="tabs"
    :context="AppContext.MSP"
    :full-details-config="fullDetailsConfig"
    :comments="comments"
    :comments-loading="commentsLoading"
    :total-comments="totalComments"
    :comment-not-allowed="commentNotAllowed"
    :activity-logs-loading="activityLogsLoading"
    :activity-logs-access-restricted="activityLogsAccessRestricted"
    :hide-all-activity-link="hideAllActivityLink"
    @tab-click="onTabClick"
    @load-more-activity-logs="loadMoreActivityLogs"
    @add-comment="handleAddComment"
    @load-more-comments="handleLoadMoreComments"
    @undo-activity-log="undoActivityLog"
  >
    <template #header="{ ticketDetails }">
      <msp-ticket-preview-header :ticket-details="ticketDetails" />
    </template>
  </ticket-preview-base>
</template>

<script lang="ts">
import type { TicketsScope } from "@/_store/roles.module";
import { defineComponent, type PropType, toRefs } from "vue";
import { storeToRefs } from "pinia";
import { type MspTicket, useMspTicketsStore } from "@/_store/msp/tickets/msp-tickets.module.ts";
import { useMspTicketCommentsStore } from "@/_store/msp/tickets/msp-ticket-comments.module.ts";
import MspTicketPreviewHeader from "@/components/msp/tickets/MspTicketPreviewHeader.vue";
import TicketPreviewBase from "@/components/tickets/TicketPreviewBase.vue";
import { mspTicketFullDetailsConfig } from "@/components/tickets/ticket-preview-details/full-details/helpers/ticket-full-details.config.ts";
import { AppContext } from "@/types";
import { isMspAccessRestricted, isMspActionRestricted } from "@/_helpers/msp-permissions.ts";
import { GTMParams } from "@/constants/gtm.ts";
import {
  useTicketPreview,
  type TicketPreviewAdapter,
} from "@/composables/tickets/useTicketPreview";
import { RolePermissionsScope, WorkspaceManagementScopeSections } from "@/constants/roles.ts";

export default defineComponent({
  components: {
    MspTicketPreviewHeader,
    TicketPreviewBase,
  },
  props: {
    ticket: {
      type: Object as PropType<MspTicket | null>,
      default: null,
    },
  },
  setup(props) {
    const { ticket } = toRefs(props);

    const ticketsStore = useMspTicketsStore();
    const commentsStore = useMspTicketCommentsStore();

    const { ticketDetailsLoading, ticketDetails, activityLogsLoading } = storeToRefs(ticketsStore);
    const { comments, loading: commentsLoading, totalComments } = storeToRefs(commentsStore);

    const isCommentRestricted = () => {
      const moduleName = ticketDetails.value?.moduleName as keyof TicketsScope | undefined;
      if (!moduleName) return true;

      return isMspActionRestricted(RolePermissionsScope.TICKETS, moduleName, "commentTickets");
    };

    /**
     * Adapter provides MSP-specific behavior to the useTicketPreview composable.
     * MSP context differs from single workspace by requiring workspaceId for all operations
     * and using MSP-specific permission checks that work across multiple workspaces.
     */
    const adapter: TicketPreviewAdapter<MspTicket, typeof ticketDetails.value> = {
      // Bind MSP-specific store methods
      ticketsStore: {
        getTicketDetails: ticketsStore.getMspTicketDetails,
        getTicketActivityLogs: ticketsStore.getMspTicketActivityLogs,
        resetActivityLogs: ticketsStore.resetActivityLogs,
        undoActivityLog: ticketsStore.undoActivityLog,
      },
      commentsStore: {
        resetPagination: commentsStore.resetPagination,
        getComments: commentsStore.getComments,
        addComment: commentsStore.addComment,
        // Wrapper to enforce required workspaceId for MSP context
        // The adapter interface allows optional workspaceId, but MSP store requires it
        getNextPage: async (params: { eventId: string; workspaceId?: string }) => {
          if (!params.workspaceId) {
            throw new Error("workspaceId is required for MSP comments");
          }
          return commentsStore.getNextPage({
            eventId: params.eventId,
            workspaceId: params.workspaceId,
          });
        },
      },
      // Pass reactive refs from MSP stores
      ticketDetailsLoading,
      ticketDetails,
      activityLogsLoading,
      comments,
      commentsLoading,
      totalComments,
      // MSP context identifiers
      gtmContext: GTMParams.MSP,
      appContext: AppContext.MSP,
      // MSP-specific permission checks that consider multi-workspace hierarchy
      isActivityLogsRestricted: () =>
        isMspAccessRestricted(
          RolePermissionsScope.WORKSPACE_MANAGEMENT,
          WorkspaceManagementScopeSections.ACTIVITY_LOGS
        ),
      isCommentRestricted,
    };

    const previewState = useTicketPreview(ticket, adapter);

    return {
      ...previewState,
      fullDetailsConfig: mspTicketFullDetailsConfig,
      // MSP always hides the "view all activity" link
      hideAllActivityLink: true,
      AppContext,
    };
  },
});
</script>

<style scoped lang="scss">
:deep(*) {
  .text-primary {
    .icon-warning-xs:before {
      color: rgb(var(--v-theme-primary));
    }
  }
}
</style>
